import { createTheme } from "@mui/material/styles";
import {
  brand,
  neutral,
  accent,
  text,
  status,
  radius,
  shadow,
  fontFamily,
  typeScale,
} from "./tokens";

// ============================================================================
// CABLE CRM THEME — the executable design system. MUI components inherit these
// defaults automatically, so consistency (radius, spacing, color, type) is
// enforced centrally instead of via ad-hoc inline sx on every page.
//
// Look: ONE continuous dark purple surface. The sidebar/navbar gradient and the
// content area now resolve to the same brand purple (#302b63), with cards and
// tables LIFTED above that ground rather than punched out of it in white.
// Anything that has to be picked out at a glance — page titles, section
// headings, table column headers, dialog titles, and the highlighted/primary
// buttons — is the brand amber; body copy is white, secondary copy white at
// 72%. KPI tiles are colour-coded from `palette.accent`.
//
// `mode: "dark"` matters beyond the palette: it is what makes MUI's own
// derived values (action.hover/selected/disabled, the DataGrid's internals,
// input and checkbox states) compute light-on-dark instead of the reverse.
// Data-dense but scannable (professional CRM).
// ============================================================================

const t = (spec) => ({
  fontSize: spec.size,
  fontWeight: spec.weight,
  lineHeight: spec.lh,
});

const theme = createTheme({
  breakpoints: {
    values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 },
  },

  palette: {
    mode: "dark",
    primary: { main: brand.purple, dark: brand.purpleDeep, light: brand.purpleSoft, contrastText: text.onDark },
    secondary: { main: brand.amber, dark: brand.amberDark, contrastText: brand.onAmber },
    success: { main: status.success, contrastText: "#fff" },
    warning: { main: status.warning, contrastText: "#fff" },
    error: { main: status.error, contrastText: "#fff" },
    info: { main: status.info, contrastText: "#fff" },
    background: { default: neutral.bg, paper: neutral.surface },
    text: { primary: text.primary, secondary: text.secondary, disabled: text.disabled },
    // Custom slot: the amber used for every heading-level element. Kept out of
    // `text` proper so `color="text.primary"` never silently turns amber.
    heading: text.heading,
    divider: neutral.border,
    // custom slots used across the app
    brand,
    neutral,
    accent,
    statusBg: {
      success: status.successBg,
      warning: status.warningBg,
      error: status.errorBg,
      info: status.infoBg,
    },
  },

  shape: { borderRadius: radius.sm },

  typography: {
    fontFamily,
    // Tabular figures keep data columns aligned (numbers don't jitter).
    fontFeatureSettings: '"tnum" 1',
    // Every heading variant is amber by default — see the `heading` palette
    // slot. A page that genuinely wants a white heading sets `color` itself.
    h1: { ...t(typeScale.h1), color: text.heading },
    h2: { ...t(typeScale.h2), color: text.heading },
    h3: { ...t(typeScale.h3), color: text.heading },
    h4: { ...t(typeScale.h4), color: text.heading },
    h5: { ...t(typeScale.h5), color: text.heading },
    h6: { ...t(typeScale.h6), color: text.heading },
    subtitle1: t(typeScale.subtitle),
    subtitle2: { ...t(typeScale.subtitle), fontWeight: 500 },
    body1: t(typeScale.body1),
    body2: t(typeScale.body2),
    caption: t(typeScale.caption),
    button: { ...t(typeScale.button), textTransform: "none" },
  },

  components: {
    // ── Global ──────────────────────────────────────────────────────────────
    MuiCssBaseline: {
      styleOverrides: {
        // `color-scheme: dark` is what makes the BROWSER's own chrome follow the
        // theme: native date/time pickers (used on every from/to filter),
        // scrollbars, and form-control defaults. Without it those render light
        // on this dark ground and look pasted on.
        ":root": { colorScheme: "dark" },
        body: {
          backgroundColor: neutral.bg,
          color: text.primary,
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
        },
        "*": { boxSizing: "border-box" },
      },
    },

    // ── Surfaces ─────────────────────────────────────────────────────────────
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: "none" },
        rounded: { borderRadius: radius.md },
        outlined: { borderColor: neutral.border },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          border: `1px solid ${neutral.border}`,
          boxShadow: shadow.card,
        },
      },
    },

    // ── Buttons ──────────────────────────────────────────────────────────────
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: radius.sm,
          fontWeight: 600,
          textTransform: "none",
          paddingInline: 16,
          minHeight: 40, // comfortable touch target
        },
        sizeSmall: { minHeight: 32, paddingInline: 12, fontSize: 13 },
        sizeLarge: { minHeight: 48, paddingInline: 22 },
        // The brand purple IS the page ground now, so a purple "primary"
        // button would be invisible. Every highlighted/primary button is the
        // brand amber instead — contained solid, outlined/text as amber on
        // transparent. `color="error"|"success"|"warning"` are untouched, so a
        // destructive button still reads as destructive.
        containedPrimary: {
          backgroundColor: brand.amber,
          color: brand.onAmber,
          "&:hover": { backgroundColor: brand.amberDark },
        },
        containedSecondary: { color: brand.onAmber },
        outlinedPrimary: {
          color: brand.amber,
          borderColor: "rgba(251,189,44,0.55)",
          "&:hover": { borderColor: brand.amber, backgroundColor: "rgba(251,189,44,0.10)" },
        },
        textPrimary: {
          color: brand.amber,
          "&:hover": { backgroundColor: "rgba(251,189,44,0.10)" },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: radius.sm },
        sizeSmall: { padding: 6 },
      },
    },

    // ── Inputs (16px font-size prevents iOS focus auto-zoom on mobile) ────────
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius.sm,
          backgroundColor: neutral.surface,
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: neutral.borderStrong },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: brand.amber },
        },
        input: { fontSize: 15, color: text.primary },
        notchedOutline: { borderColor: neutral.border },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          "@media (max-width:600px)": { fontSize: 16 },
        },
      },
    },
    MuiTextField: { defaultProps: { size: "small" } },
    MuiFormControl: { defaultProps: { size: "small" } },
    MuiInputLabel: {
      styleOverrides: {
        root: { fontSize: 14, color: text.secondary, "&.Mui-focused": { color: brand.amber } },
      },
    },

    // ── Chips (status pills) ─────────────────────────────────────────────────
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: radius.sm, fontWeight: 600, fontSize: 12 },
        sizeSmall: { height: 22 },
      },
    },

    // ── Tabs / toggles ───────────────────────────────────────────────────────
    MuiTab: { styleOverrides: { root: { textTransform: "none", fontWeight: 600, minHeight: 44 } } },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          borderColor: neutral.border,
          color: text.secondary,
          // The selected state used to be `brand.purple` — which is the page
          // ground here, so "selected" and "not selected" looked identical.
          // An amber wash instead, matching every other highlighted control.
          "&.Mui-selected": {
            backgroundColor: "rgba(251,189,44,0.18)",
            color: brand.amber,
            borderColor: "rgba(251,189,44,0.55)",
            "&:hover": { backgroundColor: "rgba(251,189,44,0.26)" },
          },
        },
      },
    },

    // ── Overlays ─────────────────────────────────────────────────────────────
    MuiDialog: { styleOverrides: { paper: { borderRadius: radius.lg, boxShadow: shadow.overlay } } },
    MuiDialogTitle: { styleOverrides: { root: { fontWeight: 700, fontSize: 18, color: text.heading } } },
    MuiMenu: { styleOverrides: { paper: { borderRadius: radius.md, boxShadow: shadow.raised, border: `1px solid ${neutral.border}` } } },
    MuiDrawer: { styleOverrides: { paper: { border: "none" } } },
    MuiTooltip: {
      styleOverrides: {
        // Was `text.primary`, which is white in this theme — that made every
        // tooltip white-on-white. Pin it to the darkest brand shade instead.
        tooltip: {
          backgroundColor: brand.purpleDeep,
          color: text.onDark,
          border: `1px solid ${neutral.border}`,
          fontSize: 12,
          borderRadius: radius.sm,
          padding: "6px 10px",
        },
        arrow: { color: brand.purpleDeep },
      },
    },

    // ── Tables (MUI Table + X DataGrid) ──────────────────────────────────────
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: neutral.border, fontSize: 14 },
        // SOLID amber header bar with dark text — the operator's long-standing
        // table look, carried over from the previous app. Amber-on-purple text
        // was the earlier attempt; a solid bar separates header from data far
        // more decisively on a dark ground.
        head: { backgroundColor: brand.amber, fontWeight: 700, color: brand.onAmber },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: "none",
          fontSize: 14,
          color: text.primary,
          "--DataGrid-rowBorderColor": neutral.border,
          "--DataGrid-containerBackground": neutral.surfaceAlt,
          backgroundColor: neutral.surface,
        },
        // Solid amber header bar (see MuiTableCell.head). Every glyph that
        // sits ON that bar — title, sort arrow, column menu, the separators —
        // has to flip to the dark on-amber colour too, or it stays white and
        // disappears against the amber.
        columnHeaders: { backgroundColor: brand.amber },
        columnHeader: {
          backgroundColor: brand.amber,
          "&:focus, &:focus-within": { outline: "none" },
        },
        columnHeaderTitle: { fontWeight: 700, color: brand.onAmber, letterSpacing: 0.2 },
        columnSeparator: { color: "rgba(26,26,46,0.28)" },
        sortIcon: { color: brand.onAmber },
        menuIcon: { color: brand.onAmber },
        menuIconButton: { color: brand.onAmber },
        iconButtonContainer: { color: brand.onAmber },
        columnHeaderCheckbox: { color: brand.onAmber },
        cell: { borderColor: neutral.border },
        row: { "&:hover": { backgroundColor: neutral.surfaceTint } },
        footerContainer: { borderColor: neutral.border },
        virtualScroller: { minHeight: 120 },
      },
    },

    // MUI's default link colour is `primary.main` — the brand purple, which is
    // the page ground in this theme, so every inline link (auth pages' "Forgot
    // password?" / "Create an account", etc.) rendered invisible. Amber, like
    // every other highlighted element.
    MuiLink: {
      styleOverrides: {
        root: {
          color: brand.amber,
          textDecorationColor: "rgba(251,189,44,0.4)",
          "&:hover": { color: brand.amberDark },
        },
      },
    },

    MuiDivider: { styleOverrides: { root: { borderColor: neutral.border } } },
    MuiListItemButton: { styleOverrides: { root: { borderRadius: radius.sm } } },
  },
});

export default theme;
